import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  CheckCircle2, Circle, ArrowRight, X, PartyPopper,
  User, UtensilsCrossed, CreditCard, ShoppingBag
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepIcons = {
  profile: User,
  menu: UtensilsCrossed,
  stripe: CreditCard,
  first_order: ShoppingBag,
};

export default function OnboardingBanner() {
  const { token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axios.get(`${API}/restaurant-dashboard/onboarding-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProgress(res.data);
        if (res.data.dismissed) setDismissed(true);
      } catch {
        // Silently fail
      }
    };
    fetchProgress();
  }, [token]);

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(`${API}/restaurant-dashboard/onboarding-progress/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      // Silently fail
    }
  };

  if (!progress || dismissed) return null;

  const { steps, completed, total, all_complete } = progress;
  const percentage = Math.round((completed / total) * 100);

  // All complete — congratulations
  if (all_complete) {
    return (
      <div 
        className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl"
        data-testid="onboarding-banner-complete"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <PartyPopper className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-emerald-800">Votre boutique est prête !</p>
              <p className="text-sm text-emerald-600">
                Toutes les étapes sont complétées. Vos clients peuvent maintenant commander.
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg hover:bg-emerald-100 transition-colors text-emerald-500"
            data-testid="dismiss-onboarding"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="mb-4 rounded-2xl border border-gray-200 bg-white overflow-hidden"
      data-testid="onboarding-banner"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div>
          <p className="font-semibold text-gray-900 text-sm sm:text-base">
            Activez votre boutique — {completed}/{total} étapes
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Complétez ces étapes pour recevoir vos premières commandes
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 shrink-0"
          data-testid="dismiss-onboarding"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-3">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all duration-700"
            style={{ width: `${percentage}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </div>

      {/* Steps */}
      <div className="px-2 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-1">
        {steps.map((step) => {
          const Icon = stepIcons[step.key] || Circle;
          return (
            <div
              key={step.key}
              className={`rounded-xl p-3 transition-colors ${
                step.complete
                  ? 'bg-emerald-50'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
              data-testid={`onboarding-step-${step.key}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                {step.complete ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-300 shrink-0" />
                )}
                <span className={`text-sm font-medium truncate ${
                  step.complete ? 'text-emerald-700' : 'text-gray-700'
                }`}>
                  {step.label}
                </span>
              </div>
              <p className={`text-xs truncate ${
                step.complete ? 'text-emerald-600' : 'text-gray-500'
              }`}>
                {step.hint}
              </p>
              {!step.complete && step.link && (
                <Link to={step.link}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1.5 h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 p-0"
                  >
                    Configurer
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
